import { useState } from "react";
import PageLayout from "@/components/feature/PageLayout";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import usePageMeta from "@/hooks/usePageMeta";

const FORM_ADDR = "https://readdy.ai/api/form/da20ijbk8r5te5mqsd10";

const channels = [
  { icon: "ri-mail-line", title: "邮箱投稿", text: "contact@keyoumanga.example" },
  { icon: "ri-wechat-line", title: "微信公众号", text: "可游漫画网" },
  { icon: "ri-time-line", title: "服务时间", text: "每日 09:00 - 22:00" },
];

export default function Contact() {
  usePageMeta({
    title: "联系我们 - 投稿与咨询 | 可游漫画网",
    metaDesc:
      "欢迎通过可游漫画网联系我们提交稿件、情报线索或意见反馈。无论是新游漫画化、版本更新漫画还是联动情报，我们都期待你的分享。",
  });

  const [formState, setFormState] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐检测
    const hp = String(formData.get("phone_alt") || "").trim();
    if (hp !== "") {
      setFormState("success");
      form.reset();
      return;
    }
    formData.delete("phone_alt");

    setFormState("loading");
    setFormError("");
    try {
      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const ok =
        res.ok &&
        parsed?.code === "OK" &&
        !/spam/i.test(serverMsg || "") &&
        !/form data is spam/i.test(serverMsg || "");
      if (ok) {
        setFormState("success");
        form.reset();
      } else {
        setFormState("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setFormState("error");
      setFormError("网络异常，请稍后再试。");
    }
  };

  return (
    <PageLayout>
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />

        <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <span className="manga-clip inline-flex items-center gap-2 bg-accent-500 px-3 py-1 text-sm font-bold text-foreground-950">
              <i className="ri-chat-3-line" />联系我们
            </span>
            <h1 className="mt-5 font-heading text-3xl leading-tight text-foreground-950">
              投稿、情报与意见反馈
            </h1>
            <p className="mt-4 text-base leading-relaxed text-foreground-700">
              无论是新游漫画化的第一手消息，还是精彩的联动漫画解读，或是宝贵的意见与建议，
              都欢迎通过下方表单或联系方式与我们分享。可游漫画网期待与每一位关注游戏漫画内容的读者交流。
            </p>
            <div className="mt-6 space-y-4">
              {channels.map((c) => (
                <div key={c.title} className="flex items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="manga-clip-both flex h-11 w-11 shrink-0 items-center justify-center bg-primary-500 text-background-50">
                    <i className={`${c.icon} text-lg`} />
                  </span>
                  <div>
                    <p className="text-sm font-bold text-foreground-950">{c.title}</p>
                    <p className="text-sm text-foreground-600">{c.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <SectionHeading kicker="在线留言" title="给我们留言" />

              {formState === "success" ? (
                <div className="rounded-lg bg-secondary-100 p-8 text-center">
                  <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                    <i className="ri-check-line text-2xl" />
                  </span>
                  <h3 className="mt-4 font-heading text-xl text-foreground-950">提交成功</h3>
                  <p className="mt-2 text-sm text-foreground-700">感谢你的留言，我们会尽快与你联系。</p>
                  <button
                    type="button"
                    onClick={() => setFormState("idle")}
                    className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-bold text-background-50 hover:bg-primary-400"
                  >
                    再写一条
                  </button>
                </div>
              ) : (
                <form data-readdy-form="keyou-contact" onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="mb-1.5 block text-sm font-bold text-foreground-800">您的称呼</label>
                      <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        placeholder="请输入您的称呼"
                        className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="mb-1.5 block text-sm font-bold text-foreground-800">联系邮箱</label>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        placeholder="请输入联系邮箱"
                        className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="subject" className="mb-1.5 block text-sm font-bold text-foreground-800">主题类型</label>
                    <select
                      id="subject"
                      name="subject"
                      required
                      className="w-full rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400"
                    >
                      <option value="">请选择主题</option>
                      <option value="游戏资讯投稿">游戏资讯投稿</option>
                      <option value="联动情报线索">联动情报线索</option>
                      <option value="新游漫画化投稿">新游漫画化投稿</option>
                      <option value="版本更新内容">版本更新内容</option>
                      <option value="意见反馈">意见反馈</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="message" className="mb-1.5 block text-sm font-bold text-foreground-800">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={6}
                      placeholder="请简要描述您的投稿内容或想法（500字以内）"
                      className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                    />
                  </div>
                  <input type="text" name="phone_alt" data-hp="tru" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  {formState === "error" && (
                    <p className="rounded-md bg-accent-100 px-4 py-3 text-sm text-foreground-900">
                      <i className="ri-error-warning-line mr-1" />
                      {formError}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={formState === "loading"}
                    className="manga-clip w-full whitespace-nowrap bg-primary-500 px-6 py-3 text-base font-bold text-background-50 transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  >
                    {formState === "loading" ? "提交中…" : "提交留言"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
      <BackToTop />
    </PageLayout>
  );
}